<template>
  <div class="mct-timeline" :class="{ 'mct-timeline--disabled': disabled, 'mct-timeline--compact': compact }">
    <div class="mct-timeline-header">
      <span>{{ formatSeconds(0) }}</span>
      <span>{{ formatSeconds(durationSeconds) }}</span>
    </div>

    <div
      ref="track"
      class="mct-track"
      @pointerdown="onTrackPointerDown"
    >
      <div class="mct-minor-layer" aria-hidden="true">
        <div
          v-for="marker in minorMarkers"
          :key="`mct-minor-${marker.time}`"
          class="mct-minor-marker"
          :style="{ left: marker.percent + '%' }"
        >
          <div class="mct-minor-marker-line"></div>
        </div>
      </div>

      <div
        v-for="marker in majorMarkers"
        :key="`mct-major-${marker.time}`"
        class="mct-marker"
        :style="{ left: marker.percent + '%' }"
        :title="formatSeconds(marker.time)"
      >
        <div class="mct-marker-line"></div>
        <div
          v-if="shouldShowMarkerLabel(marker)"
          class="mct-marker-label"
        >{{ formatSeconds(marker.time) }}</div>
      </div>

      <div v-if="segmentRanges.length === 0" class="mct-empty-hint" aria-hidden="true">
        <i class="ti-cut"></i>
        <span>{{ tt("makeClips.timelineEmptyHint", "Ajusta el recorte en la línea de tiempo") }}</span>
      </div>

      <div
        v-for="(range, index) in segmentRanges"
        :key="`mct-segment-${range.id}`"
        class="mct-segment"
        :class="{
          'mct-segment--active': drag.active && drag.index === index,
          'mct-segment--slim': range.width < 9,
        }"
        :style="{ left: range.left + '%', width: range.width + '%' }"
        :title="tt('vodClips.dragToMoveSelection', 'Arrastra para mover la selección')"
        @pointerdown.stop="onSegmentPointerDown(index, $event)"
      >
        <div
          class="mct-handle mct-handle--left"
          :title="tt('vodClips.adjustStartDrag', 'Ajustar inicio (arrastra)')"
          @pointerdown.stop="onHandlePointerDown(index, 'start', $event)"
        ></div>

        <div
          class="mct-handle mct-handle--right"
          :title="tt('vodClips.adjustEndDrag', 'Ajustar fin (arrastra)')"
          @pointerdown.stop="onHandlePointerDown(index, 'end', $event)"
        ></div>

      </div>
      <div v-if="visibleTipRange" class="mct-drag-tip">
        {{ formatSeconds(visibleTipRange.start) }} – {{ formatSeconds(visibleTipRange.end) }}
      </div>
    </div>

    <p v-if="!compact" class="mct-help">
      <i class="ti-hand-drag" aria-hidden="true"></i>
      {{ tt("makeClips.timelineHelp", "Arrastra los bordes o el bloque para ajustar el inicio y el fin del recorte.") }}
    </p>
  </div>
</template>

<script>
const MIN_SEGMENT_SECONDS = 5;
const DEFAULT_SEGMENT_SECONDS = 300;
const NICE_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600];

export default {
  name: "MakeClipsSegmentsTimeline",
  props: {
    durationSeconds: {
      type: Number,
      required: true,
    },
    segments: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      drag: {
        active: false,
        mode: null,
        index: -1,
        startX: 0,
        baseStart: 0,
        baseEnd: 0,
        trackWidth: 0,
        minBound: 0,
        maxBound: 0,
      },
      dragFrameRequestId: null,
      dragPendingClientX: null,
    };
  },
  beforeDestroy() {
    this.stopDrag();
  },
  computed: {
    safeDuration() {
      const duration = Number(this.durationSeconds);
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    },
    segmentRanges() {
      const duration = this.safeDuration;
      if (duration <= 0) return [];
      return (this.segments || []).map((segment, index) => {
        const start = Math.max(0, Math.min(duration, Number(segment?.start) || 0));
        const end = Math.max(start, Math.min(duration, Number(segment?.end) || 0));
        const left = (start / duration) * 100;
        const width = Math.max(0.6, ((end - start) / duration) * 100);
        return {
          id: segment?.id != null ? segment.id : `idx-${index}`,
          start,
          end,
          left: Math.max(0, Math.min(100, left)),
          width: Math.min(100 - Math.max(0, Math.min(100, left)), width),
        };
      });
    },
    activeDragRange() {
      return this.drag.active ? this.segmentRanges[this.drag.index] : null;
    },
    visibleTipRange() {
      if (this.compact) return this.segmentRanges[0];
      return this.activeDragRange;
    },
    markerStepSeconds() {
      const duration = this.safeDuration;
      if (duration <= 0) return 0;
      const target = duration / 6;
      const step = NICE_STEPS.find((candidate) => candidate >= target);
      return step || NICE_STEPS[NICE_STEPS.length - 1];
    },
    majorMarkers() {
      const duration = this.safeDuration;
      const step = this.markerStepSeconds;
      if (duration <= 0 || step <= 0) return [];
      const markers = [];
      for (let current = 0; current <= duration; current += step) {
        const percent = (current / duration) * 100;
        if (percent > 97 && current !== 0) break;
        markers.push({
          time: current,
          percent: Math.max(0, Math.min(100, percent)),
        });
      }
      return markers;
    },
    minorMarkers() {
      const duration = this.safeDuration;
      const majorStep = this.markerStepSeconds;
      if (duration <= 0 || majorStep <= 0) return [];
      const step = majorStep / 5;
      if (step <= 0) return [];
      const markers = [];
      for (let current = step; current < duration; current += step) {
        if (Math.abs(current % majorStep) < 0.001) continue;
        const percent = (current / duration) * 100;
        if (percent <= 0 || percent >= 100) continue;
        markers.push({ time: Math.round(current * 1000) / 1000, percent });
        if (markers.length > 160) break;
      }
      return markers;
    },
  },
  methods: {
    tt(key, fallback, params) {
      return typeof this.$t === "function" ? this.$t(key, params) : fallback;
    },
    formatSeconds(totalSeconds) {
      const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    },
    shouldShowMarkerLabel(marker) {
      if (!marker || marker.time <= 0) return false;
      const percent = Number(marker.percent);
      // Evita solapar labels con inicio/fin del header (0:00:00 y duración total).
      return Number.isFinite(percent) && percent > 3 && percent < 96;
    },
    secondsFromClientX(clientX) {
      const track = this.$refs.track;
      if (!track || typeof track.getBoundingClientRect !== "function") return null;
      const rect = track.getBoundingClientRect();
      if (!rect.width) return null;
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      return (x / rect.width) * this.safeDuration;
    },
    getNeighborBounds(index) {
      // Vecinos por tiempo (no por índice): los inputs manuales pueden dejar
      // el arreglo desordenado y los límites por índice serían incorrectos.
      const duration = this.safeDuration;
      const current = this.segments[index];
      const currentStart = Number(current?.start) || 0;
      const currentEnd = Number(current?.end) || 0;

      let minBound = 0;
      let maxBound = duration;

      this.segments.forEach((segment, segmentIndex) => {
        if (segmentIndex === index || !segment) return;
        const start = Number(segment.start) || 0;
        const end = Number(segment.end) || 0;
        if (end <= currentStart) {
          minBound = Math.max(minBound, end + 1);
        }
        if (start >= currentEnd) {
          maxBound = Math.min(maxBound, start - 1);
        }
      });

      return {
        minBound: Math.min(minBound, duration),
        maxBound: Math.max(minBound, Math.min(maxBound, duration)),
      };
    },
    onTrackPointerDown(event) {
      if (this.disabled || this.safeDuration <= 0) return;
      const clickTime = this.secondsFromClientX(event.clientX);
      if (clickTime == null) return;

      // Find the free gap that contains the click.
      const duration = this.safeDuration;
      let gapStart = 0;
      let gapEnd = duration;
      for (let i = 0; i < this.segments.length; i++) {
        const segment = this.segments[i];
        const start = Number(segment?.start) || 0;
        const end = Number(segment?.end) || 0;
        if (clickTime >= start && clickTime <= end) return; // clicked over a segment (handled elsewhere)
        if (end < clickTime) gapStart = Math.max(gapStart, end + 1);
        if (start > clickTime) gapEnd = Math.min(gapEnd, start - 1);
      }

      const gapLength = gapEnd - gapStart;
      if (gapLength < MIN_SEGMENT_SECONDS) return;

      const length = Math.min(DEFAULT_SEGMENT_SECONDS, gapLength);
      let start = clickTime - length / 2;
      start = Math.max(gapStart, Math.min(start, gapEnd - length));
      const end = start + length;

      this.$emit("add-segment", {
        start: Math.round(start),
        end: Math.round(end),
      });
    },
    onSegmentPointerDown(index, event) {
      if (this.disabled) return;
      this.startDrag("move", index, event);
    },
    onHandlePointerDown(index, which, event) {
      if (this.disabled) return;
      this.startDrag(which === "start" ? "start" : "end", index, event);
    },
    startDrag(mode, index, event) {
      const segment = this.segments[index];
      if (!segment || this.safeDuration <= 0) return;

      const track = this.$refs.track;
      if (track && typeof track.setPointerCapture === "function") {
        try {
          track.setPointerCapture(event.pointerId);
        } catch (_error) {
          // noop
        }
      }

      const rect = track && typeof track.getBoundingClientRect === "function"
        ? track.getBoundingClientRect()
        : null;
      const bounds = this.getNeighborBounds(index);

      this.drag.active = true;
      this.drag.mode = mode;
      this.drag.index = index;
      this.drag.startX = event.clientX;
      this.drag.baseStart = Number(segment.start) || 0;
      this.drag.baseEnd = Number(segment.end) || 0;
      this.drag.trackWidth = Number(rect?.width || 0);
      this.drag.minBound = bounds.minBound;
      this.drag.maxBound = bounds.maxBound;

      window.addEventListener("pointermove", this.onDragPointerMove, { passive: false });
      window.addEventListener("pointerup", this.onDragPointerUp, { passive: true });
      window.addEventListener("pointercancel", this.onDragPointerUp, { passive: true });
    },
    onDragPointerMove(event) {
      if (!this.drag.active) return;
      event.preventDefault();
      this.dragPendingClientX = Number(event.clientX || 0);
      if (this.dragFrameRequestId != null) return;
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        this.applyDragClientX(this.dragPendingClientX);
        return;
      }
      this.dragFrameRequestId = window.requestAnimationFrame(() => {
        const pendingClientX = this.dragPendingClientX;
        this.dragFrameRequestId = null;
        this.dragPendingClientX = null;
        this.applyDragClientX(pendingClientX);
      });
    },
    applyDragClientX(clientX) {
      if (!this.drag.active) return;
      const duration = this.safeDuration;
      const trackWidth = Number(this.drag.trackWidth || 0);
      if (trackWidth <= 0 || duration <= 0) return;

      const dx = Number(clientX || 0) - Number(this.drag.startX || 0);
      const deltaSeconds = (dx / trackWidth) * duration;
      const minDuration = 1;
      const { minBound, maxBound, baseStart, baseEnd, mode, index } = this.drag;

      let nextStart = baseStart;
      let nextEnd = baseEnd;

      if (mode === "move") {
        const segmentLength = Math.max(minDuration, baseEnd - baseStart);
        nextStart = baseStart + deltaSeconds;
        nextEnd = nextStart + segmentLength;
        if (nextStart < minBound) {
          nextStart = minBound;
          nextEnd = minBound + segmentLength;
        }
        if (nextEnd > maxBound) {
          nextEnd = maxBound;
          nextStart = Math.max(minBound, maxBound - segmentLength);
        }
      } else if (mode === "start") {
        nextStart = Math.max(minBound, Math.min(baseStart + deltaSeconds, baseEnd - minDuration));
        nextEnd = baseEnd;
      } else if (mode === "end") {
        nextStart = baseStart;
        nextEnd = Math.min(maxBound, Math.max(baseEnd + deltaSeconds, baseStart + minDuration));
      }

      this.$emit("segment-change", {
        index,
        start: Math.round(nextStart),
        end: Math.round(nextEnd),
      });
    },
    onDragPointerUp() {
      if (this.dragPendingClientX != null) {
        this.applyDragClientX(this.dragPendingClientX);
      }
      const draggedIndex = this.drag.index;
      this.stopDrag();
      if (draggedIndex >= 0) {
        this.$emit("drag-end", draggedIndex);
      }
    },
    stopDrag() {
      window.removeEventListener("pointermove", this.onDragPointerMove);
      window.removeEventListener("pointerup", this.onDragPointerUp);
      window.removeEventListener("pointercancel", this.onDragPointerUp);
      if (
        this.dragFrameRequestId != null
        && typeof window !== "undefined"
        && typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(this.dragFrameRequestId);
      }
      this.dragFrameRequestId = null;
      this.dragPendingClientX = null;
      this.drag.active = false;
      this.drag.mode = null;
      this.drag.index = -1;
      this.drag.trackWidth = 0;
    },
  },
};
</script>

<style scoped>
.mct-timeline {
  border: 1px solid rgba(196, 181, 253, 0.24);
  border-radius: 16px;
  background:
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.06), transparent 44%),
    radial-gradient(circle at bottom left, rgba(168, 85, 247, 0.1), transparent 48%),
    rgba(15, 23, 42, 0.88);
  padding: 16px 16px 12px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 8px 24px rgba(15, 23, 42, 0.42);
}

.mct-timeline--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.mct-timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
  font-size: 10px;
  font-weight: 700;
  color: rgba(226, 232, 240, 0.62);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
}

.mct-timeline-header span:last-child {
  margin-left: auto;
}

.mct-track {
  position: relative;
  width: 100%;
  height: 60px;
  border-radius: 14px;
  border: 1px solid rgba(196, 181, 253, 0.22);
  background: rgba(9, 9, 11, 0.72);
  overflow: visible;
  user-select: none;
  touch-action: none;
  cursor: copy;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.35);
}

.mct-minor-layer {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  clip-path: inset(0 6px 0 6px);
  -webkit-clip-path: inset(0 6px 0 6px);
}

.mct-minor-marker {
  position: absolute;
  top: 0;
  height: 14%;
  pointer-events: none;
}

.mct-minor-marker-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(34, 211, 238, 0.42) 0%,
    rgba(168, 85, 247, 0.12) 100%
  );
}

.mct-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.mct-marker-line {
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 100%;
  background: rgba(196, 181, 253, 0.28);
  transform: translateX(-50%);
}

.mct-marker-label {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: rgba(226, 232, 240, 0.58);
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.mct-empty-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(148, 163, 184, 0.85);
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 1;
}

.mct-empty-hint i {
  color: #67e8f9;
  animation: mct-hint-pulse 2.2s ease-in-out infinite;
}

@keyframes mct-hint-pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mct-drag-tip,
  .mct-empty-hint i {
    animation: none;
  }

  .mct-segment,
  .mct-handle {
    transition: none;
  }
}

.mct-segment {
  position: absolute;
  top: 8px;
  bottom: 8px;
  border-radius: 11px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.82) 0%, rgba(6, 182, 212, 0.72) 100%);
  border: 2px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    0 8px 24px rgba(168, 85, 247, 0.38),
    inset 0 0 24px rgba(255, 255, 255, 0.08),
    0 0 20px rgba(34, 211, 238, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: grab;
  z-index: 2;
  overflow: visible;
  transition: box-shadow 0.25s ease, filter 0.25s ease, border-color 0.25s ease;
}

.mct-segment:hover {
  border-color: rgba(255, 255, 255, 0.34);
  filter: brightness(1.04);
}

.mct-segment:active {
  cursor: grabbing;
}

.mct-segment--active {
  z-index: 4;
  filter: brightness(1.07);
  box-shadow:
    0 10px 28px rgba(168, 85, 247, 0.48),
    inset 0 0 28px rgba(255, 255, 255, 0.1),
    0 0 28px rgba(34, 211, 238, 0.28);
}

.mct-segment-label {
  font-size: 11px;
  font-weight: 800;
  color: #ffffff;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
  pointer-events: none;
  flex: 1;
  order: 2;
  text-align: center;
  padding: 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  letter-spacing: 0.03em;
}

.mct-segment--slim .mct-segment-label {
  display: none;
}

.mct-handle {
  position: relative;
  flex-shrink: 0;
  width: 12px;
  min-width: 12px;
  height: 38px;
  min-height: 38px;
  border-radius: 7px;
  background: linear-gradient(180deg, #ffffff 0%, #e0f2fe 100%);
  border: 2px solid rgba(34, 211, 238, 0.65);
  cursor: ew-resize;
  z-index: 3;
  box-shadow:
    0 2px 10px rgba(34, 211, 238, 0.35),
    0 0 14px rgba(168, 85, 247, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mct-handle::after {
  content: "";
  width: 2px;
  height: 14px;
  background: linear-gradient(180deg, #a855f7, #06b6d4);
  border-radius: 2px;
  opacity: 0.85;
}

.mct-handle:hover {
  border-color: rgba(34, 211, 238, 0.9);
  box-shadow:
    0 4px 16px rgba(34, 211, 238, 0.45),
    0 0 22px rgba(168, 85, 247, 0.35);
  transform: scale(1.08);
}

.mct-handle--left {
  order: 1;
}

.mct-handle--right {
  order: 3;
}

.mct-segment--slim .mct-handle {
  width: 9px;
  min-width: 9px;
}

.mct-drag-tip {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(34, 211, 238, 0.35);
  background: rgba(2, 6, 23, 0.92);
  color: #67e8f9;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 6px 16px rgba(34, 211, 238, 0.16);
  animation: mct-tip-pop 0.22s ease-out;
}

@keyframes mct-tip-pop {
  from {
    opacity: 0;
    filter: blur(3px);
  }
  to {
    opacity: 1;
    filter: blur(0);
  }
}

.mct-help {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  color: rgba(148, 163, 184, 0.8);
  font-size: 0.76rem;
}

.mct-help i {
  color: #a78bfa;
}

/* --- Modo compacto: vista pequeña de solo lectura (p. ej. paso de confirmación) --- */
.mct-timeline--compact {
  padding: 10px 12px 12px;
  border-radius: 12px;
}

.mct-timeline--compact .mct-timeline-header {
  margin-bottom: 34px;
  font-size: 9px;
}

.mct-timeline--compact .mct-track {
  height: 30px;
  border-radius: 9px;
  cursor: default;
}

.mct-timeline--compact .mct-drag-tip {
  top: -29px;
  animation: none;
}

.mct-timeline--compact .mct-marker-label {
  display: none;
}

.mct-timeline--compact .mct-segment {
  top: 5px;
  bottom: 5px;
  border-radius: 7px;
  border-width: 1.5px;
}

.mct-timeline--compact .mct-segment-label {
  font-size: 9px;
}

.mct-timeline--compact .mct-handle {
  width: 10px;
  min-width: 10px;
  height: 22px;
  min-height: 22px;
  border-radius: 5px;
}

@media (max-width: 640px) {
  .mct-track {
    height: 52px;
  }

  .mct-handle {
    width: 14px;
    min-width: 14px;
    height: 32px;
    min-height: 32px;
  }

  .mct-marker-label {
    font-size: 9px;
  }
}
</style>
